/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable react/prop-types */
import React from 'react';
import classNames from 'classnames';
import styles from './index.module.scss';

export interface TableProps extends React.TableHTMLAttributes<HTMLTableElement> {
  containerClassName?: string;
  containerStyle?: React.CSSProperties;
}

export type TableHeaderProps = React.HTMLAttributes<HTMLTableSectionElement>;
export type TableBodyProps = React.HTMLAttributes<HTMLTableSectionElement>;
export type TableFooterProps = React.HTMLAttributes<HTMLTableSectionElement>;
export type TableRowProps = React.HTMLAttributes<HTMLTableRowElement>;
export type TableHeadProps = React.ThHTMLAttributes<HTMLTableCellElement>;
export type TableCellProps = React.TdHTMLAttributes<HTMLTableCellElement>;
export type TableCaptionProps = React.HTMLAttributes<HTMLTableCaptionElement>;

// Table 提供带横向滚动容器的基础表格外壳，方便在弹窗内复用当前 VIP 表格视觉。
const Table = React.forwardRef<HTMLTableElement, TableProps>((
  {
    className,
    containerClassName,
    containerStyle,
    children,
    ...props
  },
  ref,
) => {
  return (
    <div className={classNames(styles.tableContainer, containerClassName)} style={containerStyle}>
      <table ref={ref} className={classNames(styles.table, className)} {...props}>
        {children}
      </table>
    </div>
  );
});

// TableHeader 承载表头区域，保持与 shadcn table primitives 相同的组合方式。
const TableHeader = React.forwardRef<HTMLTableSectionElement, TableHeaderProps>((
  { className, ...props },
  ref,
) => {
  return <thead ref={ref} className={classNames(styles.tableHeader, className)} {...props} />;
});

// TableBody 承载主体数据行。
const TableBody = React.forwardRef<HTMLTableSectionElement, TableBodyProps>((
  { className, ...props },
  ref,
) => {
  return <tbody ref={ref} className={classNames(styles.tableBody, className)} {...props} />;
});

// TableFooter 承载汇总行，沿用主体边框和字体节奏。
const TableFooter = React.forwardRef<HTMLTableSectionElement, TableFooterProps>((
  { className, ...props },
  ref,
) => {
  return <tfoot ref={ref} className={classNames(styles.tableFooter, className)} {...props} />;
});

// TableRow 表示一行数据，默认不提供 hover 色以符合当前价格说明表的静态展示状态。
const TableRow = React.forwardRef<HTMLTableRowElement, TableRowProps>((
  { className, ...props },
  ref,
) => {
  return <tr ref={ref} className={classNames(styles.tableRow, className)} {...props} />;
});

// TableHead 表示表头单元格，封装当前 Figma 表头底色和弱文字层级。
const TableHead = React.forwardRef<HTMLTableCellElement, TableHeadProps>((
  { className, ...props },
  ref,
) => {
  return <th ref={ref} className={classNames(styles.tableHead, className)} {...props} />;
});

// TableCell 表示普通单元格，支持 colSpan / rowSpan 等原生表格能力。
const TableCell = React.forwardRef<HTMLTableCellElement, TableCellProps>((
  { className, ...props },
  ref,
) => {
  return <td ref={ref} className={classNames(styles.tableCell, className)} {...props} />;
});

// TableCaption 提供可选说明文本，默认使用低强调文字。
const TableCaption = React.forwardRef<HTMLTableCaptionElement, TableCaptionProps>((
  { className, ...props },
  ref,
) => {
  return <caption ref={ref} className={classNames(styles.tableCaption, className)} {...props} />;
});

Table.displayName = 'Table';
TableHeader.displayName = 'TableHeader';
TableBody.displayName = 'TableBody';
TableFooter.displayName = 'TableFooter';
TableRow.displayName = 'TableRow';
TableHead.displayName = 'TableHead';
TableCell.displayName = 'TableCell';
TableCaption.displayName = 'TableCaption';

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
};
